import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  MessageCircle, Send, ArrowLeft, Truck, CheckCircle,
  HelpCircle, AlertCircle, Lightbulb, Headphones
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useRegion } from '@/context/RegionContext';
import { RegionSelector } from '@/components/RegionSelector';
import api from '@/services/api';

const Suporte = () => {
  const navigate = useNavigate();
  const { config } = useRegion();
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    tipo: 'suporte',
    assunto: '',
    conteudo: ''
  });
  const [loading, setLoading] = useState(false);
  const [enviado, setEnviado] = useState(false);
  
  const tiposMensagem = [
    { id: 'suporte', icon: Headphones, label: 'Suporte Técnico', color: 'blue' },
    { id: 'duvida', icon: HelpCircle, label: 'Dúvida', color: 'purple' },
    { id: 'reclamacao', icon: AlertCircle, label: 'Reclamação', color: 'red' },
    { id: 'sugestao', icon: Lightbulb, label: 'Sugestão', color: 'green' }
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.assunto || !formData.conteudo) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/mensagens', {
        remetente_nome: formData.nome,
        remetente_email: formData.email || null,
        remetente_tipo: 'visitante',
        assunto: formData.assunto,
        conteudo: formData.conteudo,
        tipo: formData.tipo,
        regiao: config.code
      });
      
      setEnviado(true);
      toast.success('Mensagem enviada com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  if (enviado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Mensagem Enviada!
          </h1>
          
          <p className="text-gray-600 mb-6">
            Recebemos sua mensagem e responderemos em breve. 
            Obrigado por entrar em contacto!
          </p>
          
          <div className="space-y-3">
            <Button 
              onClick={() => {
                setEnviado(false);
                setFormData({ nome: '', email: '', tipo: 'suporte', assunto: '', conteudo: '' });
              }}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              Enviar Outra Mensagem
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-rota017.png" alt="Rota 017" className="w-10 h-10 rounded-xl object-cover" />
            <span className="text-xl font-bold text-gray-900">Rota 017</span>
          </Link>
          
          <div className="flex items-center gap-3">
            <RegionSelector variant="minimal" />
            <Button variant="outline" onClick={() => navigate('/login')}>
              Entrar
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Voltar */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-500 hover:text-orange-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao início
        </Link>
        
        <div className="grid md:grid-cols-5 gap-8">
          {/* Info */}
          <div className="md:col-span-2">
            <div className="sticky top-24">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Fale Conosco</h1>
                  <p className="text-sm text-gray-500">{config.flag} {config.name}</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                Tem dúvidas, sugestões ou precisa de ajuda? 
                Envie sua mensagem e nossa equipe responderá o mais breve possível.
              </p>
              
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-medium text-orange-900 mb-2">Horário de Atendimento</h3>
                <p className="text-sm text-orange-700">
                  {config.code === 'PT' 
                    ? 'Segunda a Sexta: 9h às 18h (Hora de Lisboa)'
                    : 'Segunda a Sexta: 9h às 18h (Horário de Brasília)'}
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  Resposta em até 24 horas úteis
                </p>
              </div>
            </div>
          </div>
          
          {/* Form */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tipo de mensagem */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Tipo de Mensagem
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {tiposMensagem.map((tipo) => (
                      <button
                        key={tipo.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, tipo: tipo.id })}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          formData.tipo === tipo.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <tipo.icon className={`w-5 h-5 ${
                          formData.tipo === tipo.id ? 'text-orange-600' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm font-medium ${
                          formData.tipo === tipo.id ? 'text-orange-600' : 'text-gray-700'
                        }`}>
                          {tipo.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seu Nome *
                  </label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Como podemos chamá-lo?"
                    className="h-12"
                  />
                </div>
                
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail (opcional)
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Para recebermos a resposta"
                    className="h-12"
                  />
                </div>
                
                {/* Assunto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assunto *
                  </label>
                  <Input
                    value={formData.assunto}
                    onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                    placeholder="Resumo da sua mensagem"
                    className="h-12"
                  />
                </div>
                
                {/* Mensagem */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem *
                  </label>
                  <Textarea
                    value={formData.conteudo}
                    onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
                    placeholder="Descreva sua dúvida, sugestão ou problema com detalhes..."
                    rows={5}
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-orange-600 hover:bg-orange-700"
                >
                  {loading ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Enviar Mensagem
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Suporte;
